import { useEffect, useState } from 'react';
import Snackbar from '@mui/material/Snackbar';
import { canUpdate } from '../../updates/SafeUpdate.ts';
import { downloadQueueManager } from '../../download/DownloadQueueManager.ts';
import { yomiriDatabase } from '../../storage/YomiriDatabase.ts';
import { isLocalDownloadActive } from '../../storage/LocalChapters.ts';

declare const __YOMIRI_BUILD_ID__: string;

/**
 * YomiriUpdater:
 * Sistema autónomo de actualización que NO depende de Service Workers ni Cloudflare Workers.
 * Realiza un polling directo HTTP sobre `/version.json` con cache-busting (?t=timestamp).
 * Al detectar una versión nueva, inicia la cuenta regresiva visible de 5 segundos
 * (respetando si el usuario está leyendo o viendo anime) y aplica la actualización
 * mediante un reload limpio que purga las cachés del navegador.
 */
export function YomiriUpdater({ section }: { section: string }) {
  const [waiting, setWaiting] = useState(false);
  const [seconds, setSeconds] = useState<number | null>(null);

  useEffect(() => {
    let disposed = false;

    const activate = () => {
      // Si hay un service worker heredado en el navegador, desregistrarlo para no bloquear el reload
      if ('serviceWorker' in navigator) {
        navigator.serviceWorker.getRegistrations().then((registrations) => {
          for (const reg of registrations) {
            void reg.unregister();
          }
        }).catch(() => {});
      }
      // Forzar recarga completa limpiando la memoria del navegador
      window.location.reload();
    };

    window.addEventListener('yomiri-activate-update', activate);

    let checking = false;
    const checkVersion = async () => {
      if (checking || document.hidden || !navigator.onLine) return;
      checking = true;
      try {
        // Petición directa con no-cache y timestamp para garantizar que no hay intermediarios
        const response = await fetch(`/version.json?_t=${Date.now()}`, {
          cache: 'no-store',
          headers: { 'Cache-Control': 'no-cache', Pragma: 'no-cache' },
          signal: AbortSignal.timeout(8000),
        });

        if (response.ok) {
          const data = (await response.json()) as { version?: string };
          const remoteVersion = data.version;
          const currentVersion = typeof __YOMIRI_BUILD_ID__ !== 'undefined' ? __YOMIRI_BUILD_ID__ : '';

          if (remoteVersion && currentVersion && remoteVersion !== currentVersion) {
            if (!disposed) {
              setWaiting(true);
            }
          }
        }
      } catch {
        // Si no hay conexión o falla la red, mantener la sesión actual sin molestar
      } finally {
        checking = false;
      }
    };

    // Comprobación inicial al montar
    void checkVersion();

    // Comprobación periódica cada 3 minutos y al volver a enfocar la pestaña
    const timer = window.setInterval(() => void checkVersion(), 3 * 60 * 1000);
    document.addEventListener('visibilitychange', checkVersion);

    return () => {
      disposed = true;
      clearInterval(timer);
      document.removeEventListener('visibilitychange', checkVersion);
      window.removeEventListener('yomiri-activate-update', activate);
    };
  }, []);

  useEffect(() => {
    if (!waiting) return;

    let count = 5;
    let preparing = false;
    let disposed = false;
    let retryAt = 0;

    const safe = () =>
      canUpdate({
        section,
        hidden: document.hidden,
        editing: !!document.activeElement?.matches('input,textarea,select,[contenteditable=true]'),
        dialog: !!document.querySelector('[role=dialog],dialog[open],.cl-modalBackdrop'),
        downloading:
          isLocalDownloadActive() ||
          downloadQueueManager
            .getQueue()
            .some((item) => ['QUEUED', 'DOWNLOADING', 'PAUSED'].includes(item.status)),
        playing: Array.from(document.querySelectorAll('video,audio')).some(
          (media) => !(media as HTMLMediaElement).paused,
        ),
      });

    const tick = async () => {
      if (preparing || Date.now() < retryAt) return;

      // Si el usuario está leyendo o con video abierto, esperar y reiniciar conteo a 5
      if (!safe()) {
        count = 5;
        setSeconds(null);
        return;
      }

      setSeconds(count);

      if (count-- > 0) return;

      preparing = true;
      try {
        // Guardar transacciones de IndexedDB pendientes antes de reiniciar
        const db = await yomiriDatabase.init();
        await new Promise<void>((resolve, reject) => {
          const tx = db.transaction(['mangas', 'chapters', 'history', 'settings'], 'readwrite');
          tx.oncomplete = () => resolve();
          tx.onerror = () => reject(tx.error);
          tx.onabort = () => reject(tx.error);
        });

        if (!disposed && safe()) {
          window.dispatchEvent(new Event('yomiri-activate-update'));
        }
      } catch {
        retryAt = Date.now() + 60000;
      } finally {
        preparing = false;
        count = 5;
        if (!disposed) setSeconds(null);
      }
    };

    const timer = window.setInterval(() => void tick(), 1000);

    return () => {
      disposed = true;
      clearInterval(timer);
      setSeconds(null);
    };
  }, [waiting, section]);

  return (
    <Snackbar
      open={seconds !== null}
      message={`Nueva versión lista. Yomiri se actualizará en ${seconds} s.`}
      anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
    />
  );
}
